import EventIcon from '@mui/icons-material/Event';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import EditNotificationsIcon from '@mui/icons-material/EditNotifications';
import PublishIcon from '@mui/icons-material/Publish';
import ReplayIcon from '@mui/icons-material/Replay';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined';
import ForumOutlinedIcon from '@mui/icons-material/ForumOutlined';
import PersonAddOutlinedIcon from '@mui/icons-material/PersonAddOutlined';
import EventAvailableOutlinedIcon from '@mui/icons-material/EventAvailableOutlined';
import RateReviewOutlinedIcon from '@mui/icons-material/RateReviewOutlined';
import HowToRegOutlinedIcon from '@mui/icons-material/HowToRegOutlined';
import PendingActionsOutlinedIcon from '@mui/icons-material/PendingActionsOutlined';
import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import { BRAND_ORANGE } from '../styles/portalTheme';
import {
  ARIA_NOTIFICATION_TYPE,
  getAriaNotificationMeta,
} from '../constants/ariaNotificationTypes';
import {
  COMMUNITY_NOTIFICATION_TYPE,
  getCommunityNotificationMeta,
} from '../constants/communityNotificationTypes';
import {
  EVENT_NOTIFICATION_TYPE,
  getEventNotificationMeta,
} from '../constants/eventNotificationTypes';
import HandshakeOutlinedIcon from '@mui/icons-material/HandshakeOutlined';
import {
  getVettingNotificationMeta,
  VETTING_NOTIFICATION_TYPE,
} from '../constants/vettingNotificationTypes';
import {
  getPartnerNotificationMeta,
  PARTNER_NOTIFICATION_TYPE,
} from '../constants/partnerNotificationTypes';
import {
  formatPushNotificationTimeLabel,
  joinNotificationBodyAndTime,
  pickNotificationCreatedAt,
} from '../components/Notifications/notificationTimeFormat';
import { resolveNotificationTitle } from './notificationDisplay';
import { resolvePortalNotificationType } from './portalNotificationRouting';

export const PUSH_BRAND_COLOR = BRAND_ORANGE;

const TYPE_ICONS = {
  [EVENT_NOTIFICATION_TYPE.PARTNER_EVENT_SUBMITTED]: EventIcon,
  [EVENT_NOTIFICATION_TYPE.PARTNER_EVENT_RESUBMITTED]: ReplayIcon,
  [EVENT_NOTIFICATION_TYPE.PARTNER_EVENT_CHANGES_REQUESTED]: EditNotificationsIcon,
  [EVENT_NOTIFICATION_TYPE.PARTNER_EVENT_APPROVED]: CheckCircleOutlineIcon,
  [EVENT_NOTIFICATION_TYPE.PARTNER_EVENT_PUBLISHED]: PublishIcon,
  [EVENT_NOTIFICATION_TYPE.EVENT_FEEDBACK_SUBMITTED]: RateReviewOutlinedIcon,
  [COMMUNITY_NOTIFICATION_TYPE.PARTNER_GROUP_JOIN_REQUEST]: GroupOutlinedIcon,
  [COMMUNITY_NOTIFICATION_TYPE.PARTNER_GROUP_CHAT_MESSAGE]: ForumOutlinedIcon,
  [COMMUNITY_NOTIFICATION_TYPE.PARTNER_GROUP_MEMBER_AUTO_JOINED]: EventAvailableOutlinedIcon,
  [COMMUNITY_NOTIFICATION_TYPE.PARTNER_GROUP_MEMBER_JOINED]: PersonAddOutlinedIcon,
  [VETTING_NOTIFICATION_TYPE.VETTING_DECISION_FINALIZED]: HowToRegOutlinedIcon,
  [VETTING_NOTIFICATION_TYPE.VETTING_MANUAL_REVIEW_REQUIRED]: PendingActionsOutlinedIcon,
  [VETTING_NOTIFICATION_TYPE.VETTING_SCORING_FAILED]: ErrorOutlineOutlinedIcon,
  [PARTNER_NOTIFICATION_TYPE.PARTNER_ONBOARDING_SUBMITTED]: HandshakeOutlinedIcon,
  [ARIA_NOTIFICATION_TYPE.ARIA_HUMAN_HANDOFF_REQUESTED]: SupportAgentIcon,
  partner_group_chat_message: ForumOutlinedIcon,
};

export const resolvePushDisplayTitle = (data = {}, notification = {}) => {
  const type = resolvePortalNotificationType({ data, ...data });
  const targetType = data.targetType;

  return (
    resolveNotificationTitle({ targetType, type }) ||
    notification.title ||
    data.title ||
    'SureSpace'
  );
};

/** Message line only (excludes timestamp). */
export const resolvePushDisplayMessage = (data = {}, title = '') => {
  const message = data.message || data.body;
  if (message && String(message).trim() && String(message).trim() !== title) {
    return String(message).trim();
  }
  return '';
};

/** @deprecated Use resolvePushDisplayMessage — kept for callers that expect the old name. */
export const resolvePushDisplayBody = resolvePushDisplayMessage;

/** OS / in-app push body: optional message + same timestamp rules as the notifications list. */
export const buildPushNotificationBody = (data = {}, title = '') => {
  const message = resolvePushDisplayMessage(data, title);
  const timeLabel = formatPushNotificationTimeLabel({ data, ...data });
  return joinNotificationBodyAndTime(message, timeLabel);
};

export const getPushNotificationTimestamp = (data = {}) => {
  const createdAt = pickNotificationCreatedAt({ data, ...data });
  const date = createdAt ? new Date(createdAt) : new Date();
  const ms = date.getTime();
  return Number.isFinite(ms) ? ms : Date.now();
};

export const getPushNotificationPresentation = (payload = {}) => {
  const data = payload?.data ?? {};
  const type = String(data.type || data.notificationType || '').toLowerCase();
  const eventMeta = getEventNotificationMeta(type);
  const communityMeta = getCommunityNotificationMeta(type);
  const vettingMeta = getVettingNotificationMeta(type);
  const partnerMeta = getPartnerNotificationMeta(type);
  const ariaMeta = getAriaNotificationMeta(type);
  const meta = communityMeta ?? eventMeta ?? vettingMeta ?? partnerMeta ?? ariaMeta;

  return {
    Icon:
      TYPE_ICONS[type] ??
      (meta
        ? communityMeta
          ? GroupOutlinedIcon
          : partnerMeta
            ? HandshakeOutlinedIcon
            : vettingMeta
              ? HowToRegOutlinedIcon
              : ariaMeta
                ? SupportAgentIcon
                : EventIcon
        : NotificationsActiveIcon),
    accentColor: meta?.accentColor ?? BRAND_ORANGE,
  };
};
